import getHeatIndex from "../../../util/get-heat-index";
import downloadCSV from "../../../scripts/download-csv";
import { formatReadingFrequency } from "../../../util/reading-frequency";
import {
  formatPipeStrapLocation,
  formatSprinklerSystemType,
  formatZipCode,
} from "./questionnaire-columns";
import { ownerNameFor } from "./sensor-data";
import { responseText } from "./sensor-response";

const downloadTableData = (dashboard) => {
  // Helper: CSV escape and wrap in quotes
  const e = (val) => {
    const s = (val === undefined || val === null) ? "" : String(val);
    return `"${s.replace(/"/g, '""')}"`;
  };

  // Helper: format number to two decimals (blank if not finite)
  const num2 = (v) => {
    const n = typeof v === "string" ? parseFloat(v) : v;
    return Number.isFinite(n) ? n.toFixed(2) : "";
  };

  // Helper: C -> F (returns number)
  const CtoF = (c) => (c * 9) / 5 + 32;

  // Temperatures go out in the user's unit, like the grid shows them
  const tempPref = window.getUserTempPref() === "C" ? "C" : "F";
  const toUserTemp = (c) => (tempPref === "C" ? c : CtoF(c));
  const TEMP_KEYS = ["temp", "probe_temp", "heat_index", "dew_point"];

  // Helper: dew point in the user's unit using Magnus formula
  const dewPoint = (tempC, rh) => {
    const T = parseFloat(tempC);
    const H = parseFloat(rh);
    if (!Number.isFinite(T) || !Number.isFinite(H)) return "";
    const a = 17.27;
    const b = 237.7;
    const gamma = (a * T) / (b + T) + Math.log(H / 100);
    const dewC = (b * gamma) / (a - gamma);
    return toUserTemp(dewC);
  };

  const structure = dashboard.sensorData();

  // Only export columns that are actually active/defined (matches grid behavior)
  const keys = (dashboard.activeColumnKeys
    ? dashboard.activeColumnKeys()
    : (dashboard.visibleColumns || [])
  ).filter((k) => !!structure[k]);

  if (!keys.length) {
    alert("No columns selected to export.");
    return;
  }

  // Export every sensor in the current view (sidebar selection + search) in
  // the grid's sort order: all of its pages, not just the one on screen
  const items = dashboard.getVisibleSensors();

  if (!items.length) {
    alert("No sensors currently visible to export.");
    return;
  }

  // Build header row from configured labels, with the unit on temperatures
  const headerLine = keys
    .map((k) => {
      const label = structure[k]?.label ?? k;
      return TEMP_KEYS.includes(k) ? `${label} (°${tempPref})` : label;
    })
    .map(e)
    .join(",");

  const groupNameFromValue = (g) => {
    if (!g) return "";
    if (typeof g === "object") return g.name || g.group_name || "";
    const gid = Number(g);
    const node = dashboard.GroupsManager?.groupsMap?.[gid];
    return node?.group_name || "";
  };

  // Map each visible column to a raw export value
  const rows = items.map((s) => {
    const vals = keys.map((k) => {
      switch (k) {
        case "sensor_id":
          return e(s.sensor_id);

        case "name":
          return e(s.sensor_name);

        case "account":
          return e(s.account_name);

        case "owner_name":
          // the name the grid shows, a borrowed Account name included
          return e(ownerNameFor(s).name);

        case "response":
          // "In progress - Freeze watch", "Snoozed - Pipe strap - by Sam Dolen"
          return e(responseText(s));

        case "group": {
          const names = Array.isArray(s.groups)
            ? s.groups.map(groupNameFromValue).filter(Boolean).join(", ")
            : "";
          return e(names);
        }

        case "status": {
          const text = (s.offline == 1) ? "Offline" : "Online";
          return e(text);
        }

        case "created": {
          const dt = s.created
            ? window.getUserTimezoneDate(s.created).format("YYYY-MM-DD HH:mm:ss")
            : "";
          return e(dt);
        }

        case "readings_every":
          // same text as the grid, e.g. "1 hr / 48 hr"
          return e(formatReadingFrequency(s, ""));

        case "last_checkin": {
          const dt = s.last_checkin
            ? window.getUserTimezoneDate(s.last_checkin).format("YYYY-MM-DD HH:mm:ss")
            : "";
          return e(dt);
        }

        case "next_checkin": {
          const dt = s.next_checkin
            ? window.getUserTimezoneDate(s.next_checkin).format("YYYY-MM-DD HH:mm:ss")
            : "";
          return e(dt);
        }

        case "temp": {
          const v = Number.isFinite(parseFloat(s.last_temp)) ? toUserTemp(parseFloat(s.last_temp)) : "";
          return e(v === "" ? "" : num2(v));
        }

        case "probe_temp": {
          const okSeries = typeof s.sensor_id === "string" && s.sensor_id.startsWith("EX");
          const tcEnabled = s.TC_M == 1;
          const raw = parseFloat(s.last_tcTemp);
          if (okSeries && tcEnabled && Number.isFinite(raw)) {
            const v = toUserTemp(raw);
            return e(num2(v));
          }
          return e("");
        }

        case "humidity": {
          const h = parseFloat(s.last_humidity);
          return e(Number.isFinite(h) ? num2(h) + "%" : "");
        }

        case "heat_index": {
          const tC = parseFloat(s.last_temp);
          const h = parseFloat(s.last_humidity);
          if (Number.isFinite(tC) && Number.isFinite(h)) {
            const tF = CtoF(tC);
            const hiF = getHeatIndex(tF, h);
            return e(num2(tempPref === "C" ? (hiF - 32) * 5 / 9 : hiF));
          }
          return e("");
        }

        case "dew_point": {
          const tC = parseFloat(s.last_temp);
          const h = parseFloat(s.last_humidity);
          if (Number.isFinite(tC) && Number.isFinite(h)) {
            return e(num2(dewPoint(tC, h)));
          }
          return e("");
        }

        case "power": {
          const text = s.last_pwr == 1 ? "USB" : "Battery";
          return e(text);
        }

        case "battery": {
          const pct =
            Number.isFinite(parseFloat(s.last_battPct))
              ? parseFloat(s.last_battPct)
              : parseFloat(window.getBatteryPercentage(s.last_voltage));
          return e(Number.isFinite(pct) ? num2(pct) : "");
        }

        case "rssi": {
          const r = parseFloat(s.rssi);
          return e(Number.isFinite(r) ? num2(r) : "");
        }

        case "alerts": {
          const count = Array.isArray(s.alerts) ? s.alerts.length : 0;
          return e(String(count));
        }

        case "pipe_strap_location":
          return e(formatPipeStrapLocation(s));

        case "sprinkler_system_type":
          return e(formatSprinklerSystemType(s));

        case "zip_code":
          return e(formatZipCode(s));

        default: {
          const sortVal = structure[k]?.sortValue ? structure[k].sortValue(s) : "";
          const asNum = parseFloat(sortVal);
          return Number.isFinite(asNum) ? e(num2(asNum)) : e(sortVal ?? "");
        }
      }
    });

    return vals.join(",");
  });

  const csv = [headerLine].concat(rows).join("\r\n");

  // Filename: enterprise_readings_[YYYY-mm-dd_HH-mm].csv
  const now = new Date();
  const pad = (n) => (typeof n === "number" ? String(n).padStart(2, "0") : "00");
  const yyyy = now.getFullYear();
  const mm = pad(now.getMonth() + 1);
  const dd = pad(now.getDate());
  const HH = pad(now.getHours());
  const ii = pad(now.getMinutes());

  const filename = `enterprise_readings_${yyyy}-${mm}-${dd}_${HH}-${ii}.csv`;

  downloadCSV(filename, csv);
};

export default downloadTableData;
